import { Component, EventEmitter, h, Listen, Prop, Event, State } from '@stencil/core';
import { FacetChangeEvent, facetChangeHandler, getData, Option, FacetUpdateEvent } from '@hpfc/fc-frontend-core';

@Component({
  tag: 'fccd-custom-select',
  styleUrl: 'custom-select.scss',
})
export class CustomSelect {
  /**
   * The data to display in the select component.
   */
  @State() data: Array<Option>;

  /**
   * The id for the element in this component.
   */
  @Prop() componentName!: string;

  /**
   * The size of the select component.
   */
  @Prop() heightSelection: 'md' | 'sm' = 'md';

  /**
   * Alignment of the label
   */
  @Prop() labelAlignment: 'left' | 'center' = 'left';

  /**
   * Size of label text
   */
  @Prop() labelSize: 'xs' | 'sm' | 'md' | 'lg' = 'xs';

  /**
   * Type of label text
   */
  @Prop() labelType: 'heading' | 'body' = 'heading';

  /**
   * The Options to display
   */
  @Prop() options: any;

  /**
   * The header text for the select.
   */
  @Prop() label?: string;

  /**
   * The heading text in the popover.
   */
  @Prop() popoverHeader?: string;

  /**
   * The body text in the popover.
   */
  @Prop() popoverText?: string;

  /**
   * The secondary heading text in the popover.
   */
  @Prop() popoverHeaderSecondary?: string;

  /**
   * The secondary body text in the popover.
   */
  @Prop() popoverTextSecondary?: string;

  /**
   * The position of the popover.
   */
  @Prop() popoverPosition?:
    | 'bottom'
    | 'bottom-end'
    | 'bottom-start'
    | 'left'
    | 'left-end'
    | 'left-start'
    | 'right'
    | 'right-end'
    | 'right-start'
    | 'top'
    | 'top-end'
    | 'top-start' = 'top';

  /**
   * Whether the field is required.
   * Default is false.
   */
  @Prop() required: boolean = false;

  /**
   * Show the optional field in the label.
   */
  @Prop() showFieldOptional: boolean = false;
  /**
   * Dispatches this event when a selection is made.
   */
  @Event() fccdFacetChangeEvent: EventEmitter<FacetChangeEvent>;

  componentWillLoad() {
    if (!this.data) {
      this.data = getData(this.options);
    }
  }

  @Listen('fccdComponentEvent', { target: 'document' })
  updateHandler(event: CustomEvent<FacetUpdateEvent>) {
    if (event?.detail?.facetOptionMap?.has(this.componentName)) {
      this.data = event.detail.facetOptionMap.get(this.componentName);
    }
  }

  handleChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;

    // Create custom event with event.detail.value to be used by the facetChangeHandler
    const customEvent = new CustomEvent('selectChange', {
      detail: { value: selectedValue },
    });

    facetChangeHandler(this.fccdFacetChangeEvent, customEvent, this.componentName, true);
  }

  render() {
    return (
      <div>
        <div class="inline">
          {this.label ? (
            <hp6-label-decorator
              size-selection={this.labelSize}
              type-selection={this.labelType}
              alignment-selection={this.labelAlignment}
              showFieldOptional={this.showFieldOptional}
            >
              <label htmlFor={this.componentName}>{this.label}</label>
            </hp6-label-decorator>
          ) : null}
          {this.popoverHeader && this.popoverText ? (
            <hp5-popover header-text={this.popoverHeader} icon-ally-label="More info" position-selection={this.popoverPosition}>
              {this.popoverText}
              {this.popoverHeaderSecondary && this.popoverTextSecondary ? (
                <div>
                  <hp6-heading size-selection="sm" tag-selection="h1">
                    {this.popoverHeaderSecondary}
                  </hp6-heading>
                  {this.popoverTextSecondary}
                </div>
              ) : null}
            </hp5-popover>
          ) : null}
        </div>
        <hp6-select-decorator height-selection={this.heightSelection}>
          <select id={this.componentName} required={this.required} onChange={event => this.handleChange(event)}>
            {this.data?.map(option => (
              <option value={option.value} selected={option.isSelected}>
                {option.label}
              </option>
            ))}
          </select>
        </hp6-select-decorator>
      </div>
    );
  }
}
